(() => {
  'use strict';

  const ROOT = () => window.Shopify?.routes?.root || '/';
  const CART_JSON_URL = () => `${ROOT()}cart.js`;
  const CART_CHANGE_URL = () => `${ROOT()}cart/change.js`;

  class RareformCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.cart = null;
      this.lastFocusedElement = null;
      this.isChanging = false;
      this.refreshTimer = null;
      this.openTimer = null;
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onCartEvent = this.onCartEvent.bind(this);
    }

    connectedCallback() {
      this.panel = this.querySelector('.rf-cart-drawer__panel');
      this.main = this.querySelector('[data-rf-cart-main]');
      this.loading = this.querySelector('[data-rf-cart-loading]');
      this.empty = this.querySelector('[data-rf-cart-empty]');
      this.filled = this.querySelector('[data-rf-cart-filled]');
      this.items = this.querySelector('[data-rf-cart-items]');
      this.footer = this.querySelector('[data-rf-cart-footer]');
      this.countLabel = this.querySelector('[data-rf-cart-count-label]');
      this.subtotal = this.querySelector('[data-rf-cart-subtotal]');
      this.errorBox = this.querySelector('[data-rf-cart-error]');

      this.addEventListener('click', (event) => this.onDrawerClick(event));
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);
      document.addEventListener('shopify:cart:lines-update', this.onCartEvent);
      document.addEventListener('cart:update', this.onCartEvent);
      document.addEventListener('cart:refresh', this.onCartEvent);

      document.documentElement.classList.add('rf-custom-cart-ready');
      this.configureOpenCartAction();
      window.setTimeout(() => this.configureOpenCartAction(), 500);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('shopify:cart:lines-update', this.onCartEvent);
      document.removeEventListener('cart:update', this.onCartEvent);
      document.removeEventListener('cart:refresh', this.onCartEvent);
    }

    configureOpenCartAction() {
      const action = window.Shopify?.actions?.openCart;
      if (!action?.configure || this.openActionConfigured) return;

      action.configure({
        handler: async () => {
          await this.open();
        },
      });

      this.openActionConfigured = true;
    }

    onDocumentClick(event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const explicitTrigger = event.target.closest('[data-rf-cart-open]');
      const cartLink = event.target.closest('a[href]');
      let isCartPageLink = false;

      if (cartLink && !cartLink.closest('rareform-cart-drawer') && !cartLink.hasAttribute('data-rf-cart-page')) {
        try {
          const linkUrl = new URL(cartLink.href, window.location.origin);
          const configuredCartPath = new URL(this.dataset.cartUrl || '/cart', window.location.origin).pathname.replace(/\/$/, '');
          isCartPageLink = linkUrl.origin === window.location.origin && linkUrl.pathname.replace(/\/$/, '') === configuredCartPath;
        } catch (_) {
          isCartPageLink = false;
        }
      }

      if (!explicitTrigger && !isCartPageLink) return;

      event.preventDefault();
      this.open();
    }

    onDocumentKeydown(event) {
      if (!this.hasAttribute('open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onCartEvent(event) {
      const action = event.action || event.detail?.action;
      const completion = event.promise && typeof event.promise.then === 'function'
        ? event.promise.catch(() => null)
        : Promise.resolve();

      completion.finally(() => {
        window.clearTimeout(this.refreshTimer);
        this.refreshTimer = window.setTimeout(() => this.refresh({ silent: !this.hasAttribute('open') }), 90);

        if (action === 'add') {
          window.clearTimeout(this.openTimer);
          this.openTimer = window.setTimeout(() => this.open(), 120);
        }
      });
    }

    async open() {
      if (!this.hasAttribute('open')) {
        this.lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        this.setAttribute('open', '');
        document.body.classList.add('rf-cart-is-open');
        requestAnimationFrame(() => this.classList.add('is-open'));
        window.setTimeout(() => this.querySelector('[data-rf-cart-close]')?.focus(), 80);
      }

      await this.refresh();
    }

    close() {
      if (!this.hasAttribute('open')) return;

      this.classList.remove('is-open');
      document.body.classList.remove('rf-cart-is-open');

      window.setTimeout(() => {
        this.removeAttribute('open');
        this.lastFocusedElement?.focus?.();
      }, 430);
    }

    async refresh({ silent = false } = {}) {
      if (!silent) this.setLoading(true);
      this.clearError();

      try {
        const response = await fetch(CART_JSON_URL(), {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
        });

        if (!response.ok) throw new Error('Unable to load your selection. Please try again.');

        this.cart = await response.json();
        this.render();
      } catch (error) {
        this.showError(error.message || 'Unable to load your selection. Please try again.');
      } finally {
        if (!silent) this.setLoading(false);
      }
    }

    async changeLine(key, quantity, itemElement) {
      if (this.isChanging) return;
      this.isChanging = true;
      this.clearError();
      itemElement?.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(CART_CHANGE_URL(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity: Math.max(0, Number(quantity) || 0) }),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.description || data.message || 'The cart could not be updated.');
        }

        this.cart = data;
        this.render();
        this.dispatchEvent(new CustomEvent('rareform:cart:updated', { bubbles: true, detail: { cart: data } }));
      } catch (error) {
        this.showError(error.message || 'The cart could not be updated.');
        await this.refresh({ silent: true });
      } finally {
        this.isChanging = false;
        itemElement?.removeAttribute('aria-busy');
      }
    }

    onDrawerClick(event) {
      const closeTrigger = event.target.closest('[data-rf-cart-close]');
      if (closeTrigger) {
        event.preventDefault();
        this.close();
        return;
      }

      const quantityButton = event.target.closest('[data-rf-quantity-action]');
      if (quantityButton) {
        event.preventDefault();
        const item = quantityButton.closest('[data-rf-line-key]');
        if (!item) return;

        const currentQuantity = Number(item.dataset.quantity) || 0;
        const nextQuantity = quantityButton.dataset.rfQuantityAction === 'increase'
          ? currentQuantity + 1
          : Math.max(0, currentQuantity - 1);

        this.changeLine(item.dataset.rfLineKey, nextQuantity, item);
        return;
      }

      const removeButton = event.target.closest('[data-rf-remove]');
      if (removeButton) {
        event.preventDefault();
        const item = removeButton.closest('[data-rf-line-key]');
        if (!item) return;
        this.changeLine(item.dataset.rfLineKey, 0, item);
      }
    }

    render() {
      const cart = this.cart;
      if (!cart) return;

      const isEmpty = !cart.item_count;
      this.loading.hidden = true;
      this.empty.hidden = !isEmpty;
      this.filled.hidden = isEmpty;
      this.footer.hidden = isEmpty;

      this.countLabel.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'ITEM' : 'ITEMS'}`;
      this.subtotal.textContent = this.formatMoney(cart.total_price, cart.currency);
      this.updateExternalCounts(cart.item_count);

      if (isEmpty) {
        this.items.innerHTML = '';
        return;
      }

      this.items.innerHTML = cart.items.map((item) => this.itemMarkup(item, cart.currency)).join('');
    }

    itemMarkup(item, currency) {
      const title = this.escapeHtml(item.product_title || item.title || 'Product');
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="rf-cart-drawer__variant">${this.escapeHtml(item.variant_title)}</p>`
        : '';
      const image = item.image
        ? `<img class="rf-cart-drawer__image" src="${this.escapeAttribute(this.imageUrl(item.image, 360))}" alt="${this.escapeAttribute(item.featured_image?.alt || title)}" loading="lazy" width="360" height="450">`
        : '<span class="rf-cart-drawer__image" aria-hidden="true"></span>';
      const productUrl = item.url || '#';
      const properties = Object.entries(item.properties || {})
        .filter(([name, value]) => value && !name.startsWith('_'))
        .map(([name, value]) => `<p class="rf-cart-drawer__property">${this.escapeHtml(name)}: ${this.escapeHtml(String(value))}</p>`)
        .join('');
      const sellingPlan = item.selling_plan_allocation?.selling_plan?.name
        ? `<p class="rf-cart-drawer__selling-plan">${this.escapeHtml(item.selling_plan_allocation.selling_plan.name)}</p>`
        : '';
      const linePrice = item.final_line_price ?? item.line_price ?? item.price * item.quantity;
      const originalLinePrice = item.original_line_price ?? linePrice;
      const comparePrice = originalLinePrice > linePrice
        ? `<span class="rf-cart-drawer__compare-price">${this.formatMoney(originalLinePrice, currency)}</span>`
        : '';

      return `
        <article class="rf-cart-drawer__item" data-rf-line-key="${this.escapeAttribute(item.key)}" data-quantity="${Number(item.quantity) || 0}">
          <a class="rf-cart-drawer__image-link" href="${this.escapeAttribute(productUrl)}" aria-label="View ${this.escapeAttribute(title)}">
            ${image}
          </a>

          <div class="rf-cart-drawer__item-content">
            <a class="rf-cart-drawer__product-title" href="${this.escapeAttribute(productUrl)}">${title}</a>
            ${variant}
            ${properties}
            ${sellingPlan}

            <div class="rf-cart-drawer__price-row">
              <span class="rf-cart-drawer__price">${this.formatMoney(linePrice, currency)}</span>
              ${comparePrice}
            </div>

            <div class="rf-cart-drawer__controls">
              <div class="rf-cart-drawer__quantity" aria-label="Quantity for ${this.escapeAttribute(title)}">
                <button type="button" data-rf-quantity-action="decrease" aria-label="Decrease quantity">−</button>
                <output aria-live="polite">${Number(item.quantity) || 0}</output>
                <button type="button" data-rf-quantity-action="increase" aria-label="Increase quantity">+</button>
              </div>

              <button type="button" class="rf-cart-drawer__remove" data-rf-remove>REMOVE</button>
            </div>
          </div>
        </article>
      `;
    }

    imageUrl(url, width) {
      try {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.set('width', String(width));
        return parsed.toString();
      } catch (_) {
        return url;
      }
    }

    formatMoney(cents, currency = 'INR') {
      const amount = Number(cents || 0) / 100;
      const isWholeCurrency = ['INR', 'JPY', 'KRW'].includes(currency);

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
          style: 'currency',
          currency,
          minimumFractionDigits: isWholeCurrency ? 0 : 2,
          maximumFractionDigits: isWholeCurrency ? 0 : 2,
        }).format(amount);
      } catch (_) {
        return `${currency} ${amount.toFixed(isWholeCurrency ? 0 : 2)}`;
      }
    }

    updateExternalCounts(count) {
      const selectors = [
        '[data-cart-count]',
        '[data-cart-count-bubble]',
        '.rf-cart-count',
        '.cart-count-bubble span[aria-hidden="true"]',
      ];

      document.querySelectorAll(selectors.join(',')).forEach((element) => {
        if (element.closest('rareform-cart-drawer')) return;
        element.textContent = String(count);
        element.toggleAttribute('hidden', count === 0);
        element.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
      });
    }

    setLoading(isLoading) {
      this.loading.hidden = !isLoading;
      if (isLoading && !this.cart) {
        this.empty.hidden = true;
        this.filled.hidden = true;
        this.footer.hidden = true;
      }
      this.panel.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    showError(message) {
      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
    }

    clearError() {
      this.errorBox.hidden = true;
      this.errorBox.textContent = '';
    }

    getFocusableElements() {
      return [...this.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = String(value ?? '');
      return div.innerHTML;
    }

    escapeAttribute(value) {
      return this.escapeHtml(value).replace(/`/g, '&#96;');
    }
  }

  if (!customElements.get('rareform-cart-drawer')) {
    customElements.define('rareform-cart-drawer', RareformCartDrawer);
  }
})();
